/**
 * "连接 Amazon" — the two sequential grants AdPraxis needs, before any Shop exists.
 *
 * The seller first picks the region to sign in through: Amazon's sign-in
 * page decides which regional account system (amazon.com, amazon.co.uk,
 * amazon.co.jp…) the grant comes from, and that account may be the only one
 * that sees the region's advertising. One region per pass, as SparkX does.
 *
 * Step 1 is the Amazon Ads grant. The profiles it returns already carry the
 * seller ids, so step 2 is one row per `(merchantId, region)` Selling Partner
 * authorization — a count the data decides, not the layout. Selling Partner
 * access stays optional: the seller can continue to Shop selection without it
 * (ADR 0023).
 */
import type { SpApiRegion } from "@adpilot/shared";
import { IconBrandAmazon, IconCheck } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { useAmazonAdsConnect } from "@/hooks/use-amazon-ads-connect";
import { SP_API_QUERY_KEY, useSpApiAuthorization } from "@/hooks/use-sp-api-authorization";
import type { AmazonAdsGrant } from "@/lib/amazon-ads-oauth";
import {
  isSellingPartnerGroupConnected,
  type SellingPartnerGroup,
  sellingPartnerGroupKey,
  sellingPartnerGroups,
} from "@/lib/selling-partner-groups";
import { fetchSpApiConnections } from "@/lib/sp-api";
import { cn } from "@/lib/utils";

/**
 * How step 2 ended when the seller moved on: every seller/region authorized,
 * some left for later, or nothing to authorize (vendor / no seller id).
 */
export type SellingPartnerOutcome = "authorized" | "skipped" | "not_applicable";

const REGIONS = ["na", "eu", "fe"] as const satisfies readonly SpApiRegion[];

interface AmazonConnectStepProps {
  /** The region to sign in through — owned by the parent alongside the grant it produces. */
  region: SpApiRegion;
  onRegionChange: (region: SpApiRegion) => void;
  /** The Ads grant, once step 1 succeeded — owned by the parent so it survives this step. */
  grant: AmazonAdsGrant | null;
  onGrant: (grant: AmazonAdsGrant) => void;
  /** Leave for Shop selection, with or without every Selling Partner grant. */
  onContinue: (sellingPartner: SellingPartnerOutcome) => void;
}

function StepRow({
  index,
  locked = false,
  title,
  description,
  action,
}: {
  index: number;
  locked?: boolean;
  title: string;
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 border-line border-b p-3 last:border-b-0">
      <span
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          locked ? "bg-subtle text-muted-foreground" : "bg-primary text-primary-foreground",
        )}
      >
        {index}
      </span>
      <span className="flex min-w-0 grow flex-col gap-0.5">
        <span className="truncate text-[13px] font-medium leading-[18px] text-ink">{title}</span>
        <span className="text-[11px] leading-[15px] text-muted-foreground">{description}</span>
      </span>
      {action}
    </div>
  );
}

function ConnectedTag({ label }: { label: string }) {
  return (
    <Badge variant="success" className="shrink-0">
      <IconCheck aria-hidden />
      {label}
    </Badge>
  );
}

export function AmazonConnectStep({
  region,
  onRegionChange,
  grant,
  onGrant,
  onContinue,
}: AmazonConnectStepProps) {
  const { t } = useTranslation("auth");
  const { t: tSettings } = useTranslation("settings");
  const ads = useAmazonAdsConnect(onGrant);
  const { beginAuthorization, pendingAuthorization, cancelAuthorization } = useSpApiAuthorization();

  const groups = grant ? sellingPartnerGroups(grant.profiles) : [];
  const connections = useQuery({
    queryKey: SP_API_QUERY_KEY,
    queryFn: fetchSpApiConnections,
    // Nothing can be authorized until the Ads grant names a seller.
    enabled: groups.length > 0,
  });
  const authorized = (group: SellingPartnerGroup) =>
    isSellingPartnerGroupConnected(group, connections.data?.connections ?? []);
  const allAuthorized = groups.every(authorized);

  let description = t("connectAmazon.description");
  if (grant && groups.length === 0) {
    // Nothing to authorize — a vendor account, or one Amazon returns without a
    // seller id. Claiming a Selling Partner grant exists would be a lie.
    description = t("connectAmazon.noSellingPartnerDescription");
  } else if (grant && !allAuthorized) {
    description = t("connectAmazon.adsConnectedDescription");
  } else if (grant) {
    description = t("connectAmazon.doneDescription");
  }

  return (
    <div className="flex flex-col gap-7">
      <div>
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <IconBrandAmazon className="size-5" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          {grant && allAuthorized ? t("connectAmazon.doneTitle") : t("connectAmazon.title")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>

      <Field>
        <FieldLabel id="amazon-connect-region-label">{t("connectAmazon.region.label")}</FieldLabel>
        {/* Locked once granted: the grant and its Shops belong to this region. */}
        <SegmentControl
          aria-labelledby="amazon-connect-region-label"
          value={region}
          widthMode="equal"
          disabled={grant !== null || ads.starting}
          onValueChange={onRegionChange}
        >
          {REGIONS.map((option) => (
            <SegmentControlItem key={option} value={option}>
              {tSettings(`sellerCentral.regions.${option}`)}
            </SegmentControlItem>
          ))}
        </SegmentControl>
      </Field>

      <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
        <StepRow
          index={1}
          title={t("connectAmazon.ads.title")}
          description={t("connectAmazon.ads.description")}
          action={
            grant ? (
              <ConnectedTag label={t("connectAmazon.connected")} />
            ) : (
              <Button size="sm" loading={ads.starting} onClick={() => ads.connect(region)}>
                {t("connectAmazon.ads.action")}
              </Button>
            )
          }
        />

        {/* Before the Ads grant, the real authorizations are not knowable yet —
            the locked row states that step 2 exists without promising a count. */}
        {!grant && (
          <StepRow
            index={2}
            locked
            title={t("connectAmazon.sellingPartner.title")}
            description={t("connectAmazon.sellingPartner.description")}
            action={
              <Button size="sm" variant="outline" disabled>
                {t("connectAmazon.sellingPartner.action")}
              </Button>
            }
          />
        )}

        {groups.map((group, index) => {
          const connected = authorized(group);
          return (
            <StepRow
              key={sellingPartnerGroupKey(group)}
              index={index + 2}
              title={t("connectAmazon.sellingPartner.title")}
              description={[
                // The account name only tells rows apart when there are several.
                ...(groups.length > 1 ? [group.accountName] : []),
                tSettings(`sellerCentral.regions.${group.region}`),
                // One authorization covers every marketplace of the seller in the
                // region — listing them keeps it from reading as per-marketplace.
                group.countryCodes.join(t("connectAmazon.sellingPartner.marketplaceSeparator")),
              ].join(" · ")}
              action={
                connected ? (
                  <ConnectedTag label={t("connectAmazon.connected")} />
                ) : (
                  <Button
                    size="sm"
                    disabled={pendingAuthorization !== null}
                    onClick={() =>
                      beginAuthorization({
                        kind: "connect-account",
                        selectionState: grant?.selectionState ?? "",
                        merchantId: group.merchantId,
                        region: group.region,
                      })
                    }
                  >
                    {t("connectAmazon.sellingPartner.action")}
                  </Button>
                )
              }
            />
          );
        })}
      </div>

      {pendingAuthorization ? (
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>{tSettings("sellerCentral.authorizing.description")}</span>
          <Button size="sm" variant="ghost" onClick={cancelAuthorization}>
            {tSettings("sellerCentral.authorizing.cancel")}
          </Button>
        </div>
      ) : null}

      {grant ? (
        <Button
          size="lg"
          variant={allAuthorized ? "default" : "outline"}
          className="w-full"
          onClick={() => {
            if (groups.length === 0) onContinue("not_applicable");
            else onContinue(allAuthorized ? "authorized" : "skipped");
          }}
        >
          {allAuthorized ? t("connectAmazon.continue") : t("connectAmazon.skip")}
        </Button>
      ) : null}
    </div>
  );
}
